import React from 'react';
import { Modal } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { enumValue, useRoleActions } from '~/util';
import { PatientContext, PatientContextType } from '~/services';
import { ConfirmPatientModal } from '~/components/Patient';
import { FileList, Upload } from '.';

export interface AttachmentProps {
    patientId: number;
    patientName: string;
    gapType: number;
    disabled?: boolean;
    uploads?: MediaFileType[];
    externalUpload?: ExternalAttachmentType | null;
    clearExternalUpload: () => void;
}

export function Attachments({
    patientId,
    patientName,
    gapType,
    disabled = false,
    uploads = [],
    externalUpload = null,
    clearExternalUpload,
}: AttachmentProps) {
    const rolePermission = useRoleActions(
        gapType === enumValue(['gap', 'type', 'Risk Gap'])
            ? 'allow_add_edit_attachments_risk_gap'
            : 'allow_add_edit_attachments_quality_gap',
        null
    );
    const [show, setShow] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [showExternalConfirmation, setShowExternalConfirmation] = React.useState(false);
    const { hasConfirmedMatch, setConfirmedMatch } = React.useContext(
        PatientContext
    ) as PatientContextType;

    function handleExternalCancel() {
        clearExternalUpload();
        setShow(false);
        setShowExternalConfirmation(false);
    }

    function confirmMatch() {
        setConfirmedMatch(true);
        setShowExternalConfirmation(false);
    }

    React.useEffect(() => {
        if (!externalUpload) return;
        // If they don't have permission, don't let the external upload go through
        if (!rolePermission) clearExternalUpload();
        // Determine whether we need to show them the confirm patient message before the upload
        if (
            rolePermission &&
            !hasConfirmedMatch &&
            (!externalUpload.autoSynced || !externalUpload.ehrOrigin)
        ) {
            setShowExternalConfirmation(true);
        }
        setShow(true);
    }, [clearExternalUpload, externalUpload, hasConfirmedMatch, rolePermission]);

    const totalUploadedSize = React.useMemo(() => {
        return uploads!.reduce((acc, current) => acc + (current?.size || 0), 0);
    }, [uploads]);

    return (
        <>
            <button
                className="btn btn-dark patient-attachments-open"
                type="button"
                onClick={() => setShow(true)}
                disabled={disabled}
            >
                <span className="badge bg-primary me-1 badge-font-90">
                    {disabled && <i className="fa-solid fa-spin fa-spinner" />}
                    {!disabled && uploads.length > 0 ? uploads.length : ''}
                </span>
                <i className="fa-solid fa-paperclip" /> Attachments
            </button>
            {showExternalConfirmation ? (
                <ConfirmPatientModal
                    patientName={patientName}
                    show={show}
                    setShow={setShow}
                    handleExternalCancel={handleExternalCancel}
                    confirmMatch={confirmMatch}
                />
            ) : (
                <BSModal
                    show={show}
                    onHide={() => setShow(false)}
                    className="attachments-modal"
                    aria-labelledby="attachments-title"
                >
                    <Modal.Header closeButton>
                        <Modal.Title id="attachments-title" role="heading" aria-level={1}>
                            Attachments
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {!rolePermission ? (
                            <div className="permissions-alert">
                                You don’t have permission to view or manage attachments on this
                                record.
                            </div>
                        ) : (
                            <div className="permissions-alert">
                                To attach files directly from another website, use{' '}
                                <a
                                    href="https://chrome.google.com/webstore/detail/zephyr-mic/ecfijpjflodeinpllfgnnglfpoailoab"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Member in Context
                                </a>
                                .
                            </div>
                        )}
                        <Upload
                            patientId={patientId}
                            gapType={gapType}
                            disabled={disabled || editing}
                            externalUpload={externalUpload}
                            clearExternalUpload={clearExternalUpload}
                            rolePermission={rolePermission}
                            mustConfirmUpload={
                                !rolePermission ||
                                (!hasConfirmedMatch &&
                                    (!externalUpload?.autoSynced || !externalUpload?.ehrOrigin))
                            }
                            totalUploadedSize={totalUploadedSize}
                        />
                        <FileList
                            uploads={uploads}
                            disabled={disabled}
                            gapType={gapType}
                            setEditing={setEditing}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => setShow(false)}
                        >
                            Close
                        </button>
                    </Modal.Footer>
                </BSModal>
            )}
        </>
    );
}
